import { Component, computed, DestroyRef, inject, input, model, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IPoint } from '@foblex/2d';
import { provideIcons } from '@ng-icons/core';
import { lucideLoaderCircle, lucideSearch } from '@ng-icons/lucide';
import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { hlm } from '@spartan-ng/helm/utils';
import { remove as removeDiac } from 'diacritics';
import { filter, map, switchMap } from 'rxjs';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { CartographieStore } from '../../../cartographie/store/cartographie.store';
import { Parcours } from '../../models/parcours.model';
import { Étape } from '../../models/étape.model';
import { EtapeService } from '../../parcours-etape/parcours-etape.service';
import { EtapeStore } from '../../parcours-etape/store/etape.store';
import { RéférentielMétierParcoursService } from '../../services/référentiel-métier-parcours.service';
import { ItemEtapeComponent } from './item-etape/item-etape.component';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-import-etape',
  imports: [
    ExpansionPanel,
    LabelRequiredDirective,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    ReactiveFormsModule,
    TrierParPipe,
    FormsModule,
    HlmSeparatorImports,
    HlmIconImports,
    HlmInputGroupImports,
    HlmRadioGroupImports,
    ItemEtapeComponent,
    HlmSpinnerImports,
  ],
  templateUrl: './import-etape.component.html',
  providers: [provideIcons({ lucideSearch, lucideLoaderCircle })],
})
export class ImportEtapeComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #parcoursService = inject(RéférentielMétierParcoursService);
  readonly #etapeService = inject(EtapeService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #étapeStore = inject(EtapeStore);

  readonly referentiel = input.required<Référentiel>();
  readonly inputParcours = input.required<Parcours>({ alias: 'parcours' });
  readonly coords = input.required<IPoint>();

  readonly cartographies = this.#cartographieStore.entities;
  readonly parcours = signal([] as Parcours[]);
  readonly etapes = signal([] as Étape[]);

  readonly form = this.#fb.group({
    cartographie: [null, [Validators.required]],
    parcours: [null, [Validators.required]],
  });

  rechercheEtape = model('');
  etapesFiltrees = computed(() =>
    this.etapes().filter(
      (x) =>
        removeDiac(x.nom ?? '')
          .toLowerCase()
          .includes(removeDiac(this.rechercheEtape().toLowerCase())) || this.etapeSelectionnee()?.id === x.id,
    ),
  );
  etapeSelectionnee = model<Étape | null>(null);

  readonly isLoading = computed(() => this.#sidepanelService.loading());

  public readonly cardClass = hlm(
    'relative block space-x-0',
    // base card styles
    'gap-md outline-stroke-neutral-light flex flex-col rounded-lg outline',
    // hover and background styles
    'bg-components-background-neutral-lightest hover:bg-components-background-neutral-lightest cursor-pointer transition-colors hover:outline-2 hover:outline-stroke-neutral-medium',
    // target the checked state properly
    '[&:has([data-checked=true])]:outline-2 [&:has([data-checked=true])]:outline-stroke-primary-main',
  );

  ngOnInit(): void {
    this.#cartographieStore.charger();

    this.cartographie.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((x) => !!x),
        switchMap((cartographie) =>
          this.#parcoursService.trouverParGroupe(cartographie.id, this.referentiel().idGroupe),
        ),
        map((x) => x.items),
      )
      .subscribe((parcours: Parcours[]) => {
        this.parcoursFC.setValue(null);
        this.parcours.set(parcours);
      });

    this.parcoursFC.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((x) => !!x),
        switchMap((parcours) => this.#etapeService.trouverTous(parcours.id)),
        map((x) => x.items),
        map((x) => x.filter((x) => !x.estPointDepart)),
      )
      .subscribe((étapes: Étape[]) => this.etapes.set(étapes));

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      const etape = this.etapeSelectionnee();
      if (etape) {
        this.#étapeStore.importerEtape({
          étapeSource: etape,
          idParcours: this.inputParcours().id,
          coords: { x: Math.floor(this.coords().x), y: Math.floor(this.coords().y) },
          next: () => this.#sidepanelService.close(),
        });
      }
    });
  }

  get cartographie() {
    return this.form.get('cartographie') as AbstractControl<Cartographie | null>;
  }

  get parcoursFC() {
    return this.form.get('parcours') as AbstractControl<Parcours | null>;
  }
}
